import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { bookingGuestsKey } from '../queryKeys';
import getSessionId from '../lib/getSessionId';
import { guestSectionText } from '@repo/lib';
import { FormDivider } from '../components/CheckoutForm/components/FormLayout/FormLayoutFields';
import {
  AllAttendeesAdded,
  ClosedRow,
  GuestsIntro,
  OpenRow,
  errorFor,
  nextOpenRow,
  withoutError,
} from '../components/GuestRows/GuestRows';
import {
  orderedTicketTypes,
  rowAsGuest,
  rowErrors,
  savedRow,
  withRowValue,
} from '../lib/guests/checkoutGuests';

const withoutKey = (object, key) => {
  const { [key]: _removed, ...rest } = object;
  return rest;
};

const hasErrors = errors =>
  Boolean(errors?.name || errors?.email || Object.keys(errors?.answers ?? {}).length);

export default function ConfirmationGuests({ payment, preview = null }) {
  const sessionId = getSessionId();
  const queryClient = useQueryClient();
  const key = bookingGuestsKey(payment._id, sessionId);
  const { data: booked } = useQuery({
    queryKey: key,
    queryFn: () => api.getBookingGuests(payment._id, sessionId),
    staleTime: Infinity,
    enabled: !preview,
  });
  const view = preview ?? booked;

  const [drafts, setDrafts] = useState({});
  const [open, setOpen] = useState(undefined);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  if (!view) return null;

  const rows = view.rows.map((row, index) => drafts[index] ?? { ...row, done: row.added });
  // The preview saves nowhere, so it works out what the API would answer for a real booking.
  const allAdded = preview ? rows.length > 0 && rows.every(row => row.done) : view.allAdded;
  const editable = preview ? !allAdded : view.editable;
  const openIndex = !editable ? -1 : open !== undefined ? open : rows.findIndex(row => !row.done);

  const change = (index, question, value) => {
    setDrafts({ ...drafts, [index]: withRowValue(rows[index], question, value) });
    if (errorFor(errors[index], question)) {
      setErrors({ ...errors, [index]: withoutError(errors[index], question) });
    }
  };

  const save = async index => {
    const rowsErrors = rowErrors({
      rows,
      index,
      questions: view.questions,
      bookerEmail: payment.customer?.email ?? '',
      emailDomains: view.emailDomains,
    });
    if (hasErrors(rowsErrors)) {
      setErrors({ ...errors, [index]: rowsErrors });
      return;
    }
    if (preview) {
      const nextRows = rows.map((row, at) => (at === index ? savedRow(row, view.questions) : row));
      setDrafts({ ...drafts, [index]: nextRows[index] });
      setErrors({ ...errors, [index]: undefined });
      setOpen(nextOpenRow(nextRows, index));
      return;
    }
    setSaving(true);
    try {
      const { ticketType: _ticketType, ...guest } = rowAsGuest(rows[index]);
      const next = await api.saveBookingGuest(payment._id, sessionId, index + 1, guest);
      queryClient.setQueryData(key, next);
      // Read state as it is now: the booker may have opened and typed in another attendee meanwhile.
      setDrafts(current => withoutKey(current, index));
      setErrors(current => ({ ...current, [index]: undefined }));
      const nextRows = next.rows.map(row => ({ ...row, done: row.added }));
      setOpen(current =>
        current === undefined || current === index ? nextOpenRow(nextRows, index) : current,
      );
    } catch (error) {
      setErrors(current => ({
        ...current,
        [index]: { email: error?.message || 'Something went wrong. Try again.' },
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cp_confirmation-guests px-4 pb-6 w-full" data-guest-rows="">
      <FormDivider />
      <GuestsIntro settings={view} aria-busy={saving || undefined}>
        {rows.map((row, index) =>
          index === openIndex ? (
            <OpenRow
              key={index}
              row={row}
              index={index}
              typeName={row.ticketTypeName}
              questions={view.questions}
              layout={view.guestFormLayout}
              errors={errors[index]}
              onChange={(question, value) => change(index, question, value)}
              onSave={() => save(index)}
              onCancel={() => {
                const { [index]: _discarded, ...otherDrafts } = drafts;
                setDrafts(otherDrafts);
                setErrors({ ...errors, [index]: undefined });
                setOpen(-1);
              }}
              saveLabel={guestSectionText(view).saveButton}
            />
          ) : (
            <ClosedRow
              key={index}
              row={row}
              index={index}
              typeName={row.ticketTypeName}
              locked={!editable}
              onEdit={() => {
                setDrafts({ ...drafts, [index]: { ...row, done: false } });
                setOpen(index);
              }}
            />
          ),
        )}
      </GuestsIntro>
      {allAdded && <AllAttendeesAdded />}
    </div>
  );
}

/** The editor preview has no booking: one blank guest per previewed ticket, saved nowhere. */
export function previewGuestsView(checkout, payment) {
  const settings = checkout?.eventDetails?.attendeeDetails;
  if (!settings?.enabled) return null;
  const typeNames = new Map(
    orderedTicketTypes(checkout).map(type => [String(type._id), type.name]),
  );
  const rows = (payment.tickets ?? []).flatMap(ticket =>
    Array.from({ length: ticket.quantity ?? 0 }, () => ({
      ticketType: ticket.ticketType,
      ticketTypeName: ticket.name ?? typeNames.get(String(ticket.ticketType)),
      name: '',
      email: '',
      answers: [],
      added: false,
    })),
  );
  return {
    questions: checkout.guestQuestions ?? [],
    guestFormLayout: checkout.attendeeFormLayout ?? null,
    rows,
    editable: true,
    emailDomains: settings.emailDomains || 'any',
    heading: settings.heading,
    description: settings.description,
    allAdded: false,
  };
}
